/**
 * How a resident relates to their unit. TWO facts, not one:
 *
 * - `relation_type` — the POSITION in the unit: proprietário, inquilino, dependente, familiar.
 *   It decides what the person may do (see the dependent-permission rules).
 * - `parentesco` — the GRAU DE PARENTESCO: pai, filho(a), mãe, or free text from "Outro". It says
 *   whose. Optional, and never implied by the position.
 *
 * Four screens each carried their own copy of this map and their own fallback chain
 * (VisitorRegistrationModal, EncomendaCheckoutModal, SelfRegistrationsPanel, OverviewTab), which
 * is how the same person could read "Proprietário" on one screen and nothing on another: where
 * the value lives depends on who wrote the record — the resident role's metadata, the unit
 * assignment nested inside it, or a flat field on the person.
 */
import { getParentescoLabel } from '../components/registration/ParentescoField';

/**
 * One vocabulary, both customer types.
 *
 * A department membership stores its position in the same `relation_type` field a unit membership
 * does, and the words are the same words: proprietário/inquilino say whether the COMPANY owns or
 * rents its space, responsável marks who runs the department, funcionário is the default. Keeping
 * two maps is how the same value read "Responsável" on the department page and nothing at all on
 * the cards beside it.
 */
const RELATION_LABELS: Record<string, string> = {
  proprietario: 'Proprietário',
  inquilino: 'Inquilino',
  dependente: 'Dependente',
  familiar: 'Familiar',
  conjuge: 'Cônjuge',
  // Empresa — department memberships (models.DepartmentRelationTypes).
  responsavel: 'Responsável',
  funcionario: 'Funcionário',
};

/**
 * The label for a stored relation value. An unmapped value is shown as-is rather than dropped:
 * a relation type added server-side must still read as *something*, since showing nothing says
 * "this person has no relation", which is a different fact.
 */
export function relationTypeLabel(value?: string | null): string | null {
  const key = (value ?? '').trim();
  if (!key) return null;
  return RELATION_LABELS[key] ?? key;
}

/**
 * The record that describes the person's relation to the unit a card is showing.
 *
 * Resolved ONCE and read whole, so `relation_type` and `parentesco` can never come from two
 * different units: a person holds one role per unit — proprietário of one, filho in another — and
 * a chip reading "Proprietário · Filho(a)" assembled from both states a relationship that exists
 * nowhere.
 *
 * The assignment for the unit the metadata NAMES wins, because it is the richest record of that
 * unit; the flat fields describe the primary unit only, and are what older rows carry.
 */
function relationSourceOf(person: any): any {
  if (!person) return null;
  const residentRole = (person.roles ?? []).find(
    (role: any) => role?.role_type === 'resident' && role?.is_active !== false,
  );
  const meta = residentRole?.role_metadata;
  const assignments: any[] = Array.isArray(meta?.unit_assignments) ? meta.unit_assignments : [];

  if (meta?.unit_id) {
    const named = assignments.find((a) => a && String(a.unit_id) === String(meta.unit_id));
    if (named) return named;
  }
  if (meta && (meta.relation_type || meta.parentesco)) return meta;
  const first = assignments.find(Boolean);
  if (first) return first;
  // Flat field only as a last resort: list endpoints project it, nested records do not.
  return residentRole ? null : person;
}

/** The raw relation value for a person, wherever the record happens to carry it. */
export function residentRelationOf(person: any): string | null {
  if (!person) return null;
  return (relationSourceOf(person)?.relation_type ?? person.relation_type ?? null) || null;
}

/** The raw grau de parentesco for a person — free text when the operator picked "Outro". */
export function residentParentescoOf(person: any): string | null {
  if (!person) return null;
  return (relationSourceOf(person)?.parentesco ?? person.parentesco ?? null) || null;
}

/** Ready to render: the person's position in the unit, or null when the record does not say. */
export function residentRelationLabel(person: any): string | null {
  return relationTypeLabel(residentRelationOf(person));
}

/**
 * Both facts in one chip — "Proprietário · Pai" — exactly as the person modal's UNIDADES card
 * says it (PersonDetailModal/tabs/OverviewTab).
 *
 * Reported: the modal said "Proprietário · Pai" and the Moradores card said "Proprietário",
 * about the same person on the same screen. Either half alone is rendered on its own; a resident
 * with no parentesco reads exactly as before.
 */
export function residentRelationChipLabel(person: any): string | null {
  const parentesco = residentParentescoOf(person);
  return [residentRelationLabel(person), parentesco ? getParentescoLabel(parentesco) : null]
    .filter(Boolean)
    .join(' · ') || null;
}

// ─── Empresa: the same question about a DEPARTMENT ───────────────────────────
//
// Everything above reads the RESIDENT role, so for a funcionário it answers null — which is why
// an empresa saw no position anywhere except the person modal and the department page, while a
// condomínio shows it beside every name.

/**
 * The raw position a person holds in a department, or null.
 *
 * Each department membership is its own `staff` PersonRole server side, so a person can be
 * Responsável of one and Funcionário of another: pass [departmentId] when the surface is about a
 * specific department, or the first active membership answers. A deactivated membership never
 * does — it is a position they no longer hold.
 */
export function staffRelationOf(person: any, departmentId?: string | null): string | null {
  if (!person) return null;
  const staffRoles: any[] = (person.roles ?? []).filter(
    (role: any) => role?.role_type === 'staff' && role?.is_active !== false,
  );
  const named = departmentId
    ? staffRoles.find((role) => String(role?.role_metadata?.department_id ?? '') === String(departmentId))
    : null;
  const chosen = named ?? staffRoles[0] ?? null;
  const relation = chosen?.role_metadata?.relation_type ?? person.relation_type ?? null;
  return (relation || null) as string | null;
}

/** Which membership a surface is asking about. Absent = whichever the person actually has. */
export interface PersonRelationScope {
  kind?: 'resident' | 'staff';
  departmentId?: string | null;
}

/**
 * The position to show for a person, whichever kind of membership they hold.
 *
 * `kind` matters for somebody who is both — a funcionário who also lives in the condomínio — and
 * reading the wrong one is exactly how a Funcionário card got relabelled by an old resident role
 * (which is why the cadastros cards used to show the relation for moradores only, rather than
 * fixing the resolution).
 */
export function personRelationLabel(person: any, scope: PersonRelationScope = {}): string | null {
  if (!person) return null;
  if (scope.kind === 'staff') return relationTypeLabel(staffRelationOf(person, scope.departmentId));
  if (scope.kind === 'resident') return residentRelationLabel(person);
  return residentRelationLabel(person) ?? relationTypeLabel(staffRelationOf(person, scope.departmentId));
}
